import { useState, useCallback, useEffect, useRef } from 'react';
import conversationService, { ConversationSummary } from 'services/conversationService';

interface UseConversationListOptions {
  userId: string | null;
  limit?: number;
  loadMoreSize?: number;
}

interface UseConversationListReturn {
  conversations: ConversationSummary[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useConversationList = ({
  userId,
  limit = 15,
  loadMoreSize = 10,
}: UseConversationListOptions): UseConversationListReturn => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const fetchConversations = useCallback(async () => {
    if (!userId) {
      console.warn('[useConversationList] No userId provided');
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const response = await conversationService.listConversations(userId, limit, 0);

      if (isMountedRef.current) {
        const conversationsArray = response.conversations || [];
        setConversations(conversationsArray);
        setTotalCount(response.total_count || 0);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (isMountedRef.current) {
        setError(err.message || 'Failed to load conversation history');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId, limit]);

  const loadMore = useCallback(async () => {
    if (!userId || loadingMore) return;

    try {
      setLoadingMore(true);
      const response = await conversationService.listConversations(
        userId,
        loadMoreSize,
        conversations.length
      );

      if (isMountedRef.current) {
        const newConversations = response.conversations || [];
        setConversations((prev) => [...prev, ...newConversations]);
        setTotalCount(response.total_count || 0);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('[useConversationList] Error loading more:', err);
    } finally {
      if (isMountedRef.current) {
        setLoadingMore(false);
      }
    }
  }, [userId, loadMoreSize, conversations.length, loadingMore]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  return {
    conversations,
    loading,
    loadingMore,
    error,
    hasMore: conversations.length < totalCount,
    refetch: fetchConversations,
    loadMore,
  };
};
